"use client";

import { useEffect, useRef, useState } from "react";
import { BookOpen, Terminal, Cloud, Clock } from "lucide-react";

const stats = [
  { icon: BookOpen, numeric: 3,  suffix: "",   text: null, label: "Courses" },
  { icon: Terminal, numeric: 95, suffix: "+",  text: null, label: "Hands-on labs" },
  { icon: Cloud,    numeric: null, suffix: "", text: "Real", label: "Azure & AWS targets" },
  { icon: Clock,    numeric: 24, suffix: " h", text: null, label: "Practical exam window" },
];

function useCountUp(target: number, duration: number, active: boolean) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    let raf: number;
    let start: number | null = null;

    const tick = (ts: number) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);

  return value;
}

function AnimatedStat({
  numeric,
  suffix,
  text,
  active,
}: {
  numeric: number | null;
  suffix: string;
  text: string | null;
  active: boolean;
}) {
  const count = useCountUp(numeric ?? 0, 1400, active && numeric !== null);
  if (text) return <>{text}</>;
  return <>{count}{suffix}</>;
}

export function BusinessStats() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setActive(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-14 bg-cb-bg border-b border-cb-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map(({ icon: Icon, numeric, suffix, text, label }) => (
            <div key={label} className="flex flex-col items-center text-center rounded-xl border border-cb-border bg-cb-surface p-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-cb-violet/10 border border-cb-violet/20 mb-4">
                <Icon className="h-7 w-7 text-cb-violet-text" />
              </div>
              <p className="text-2xl font-bold text-cb-text mb-0.5 tabular-nums">
                <AnimatedStat numeric={numeric} suffix={suffix} text={text} active={active} />
              </p>
              <p className="text-xs text-cb-muted">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
